import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { Box, Button } from "@mui/material";
import { FieldArray, Formik, Form } from "formik";
import { getJobs, getBusinesses, getContractTypes, getBusinessUnitsType } from "app/features/businessesSlice";
import { Link } from "react-router-dom";
import { styled } from "@mui/material";
import TextField from "ui/atoms/TextField";
import Select from "ui/molecules/Select";
import * as yup from "yup";
import { SurveyManageUserComponentModel, CountryAreaItem, SurveyPerson } from "types";
import LoadingButton from "@mui/lab/LoadingButton";
import PhoneInput from "ui/molecules/PhoneInput";
import AccordionControlled from "ui/organisms/Accordion";
import Company from "pages/Survey/components/Company";
import ErrorMessage from "ui/atoms/ErrorMessage";
import SaveIcon from "@mui/icons-material/Save";
import ContainerScrollable from "ui/atoms/ContainerScrollable";
import getCurrentUser from "lib/auth/pca";

const SurveyManageUsersForm = ({
  model,
  create,
  loading,
  disabled,
  handleSubmit,
  countryCodes,
  identificationTypes,
  submitButtonText = "Submit",
}: SurveyManageUserComponentModel) => {
  const dispatch = useAppDispatch();
  const { businesses, jobs, contractTypes, businessUnitsTypes } = useAppSelector((state) => state.businesses) ?? [];
  const [countries, setCountries] = useState<string[]>(["co", "ec", "us"]);
  const [countriesWithAreaCode, setCountriesWithAreaCode] = useState<any>([]);
  const currentUser = getCurrentUser();
  useEffect(() => {
    dispatch(getJobs());
    dispatch(getBusinesses());
    dispatch(getContractTypes());
    dispatch(getBusinessUnitsType());
  }, []);

  const validationRegExp = (parent: any, createError: any): boolean => {
    let { value, dialCode } = parent;
    if (value) {
      value = value?.replace(parent.dialCode, "");
      dialCode = `+${dialCode}`;
      let regex = countriesWithAreaCode.find((item: any) => item.areaCode === dialCode)?.regex;
      regex = new RegExp(regex);
      if (value?.match(regex)) {
        createError({ message: "Phone number is valid" });
        return true;
      }
    }
    return false;
  };

  const contractModel = {
    businessUnitId: "",
    contractTypeId: "",
    temporaryCompany: "",
    jobId: "",
    contractStartDate: null,
    contractEndDate: null,
    userCreation: "",
    updatedBy: "",
    canDelete: true,
  };

  const validationSchemaForCreate = yup.object().shape({
    identificationTypeId: yup.string().required("El tipo de documento es requerido"),
    identificationNumber: yup
      .number()
      .min(1, "Número de identificación inválido")
      .required("El número de documento es requerido"),
    personNames: yup.string().required("El nombre es requerido"),
    personLastName: yup.string().required("El apellido es requerido"),
    email: yup.string().required("El email es requerido").email("El email es incorrecto"),
    phone: yup.object().shape({
      value: yup
        .string()
        .required("Telefono es requerido")
        .min(10, "El teléfono no tiene la longitud correcta")
        .test("phone_async_validation", "Error en el formato del país", (value, { parent, createError }: any): any =>
          validationRegExp(parent, createError)
        ),
    }),
    ccfId: yup
      .string()
      .required("La caja de compensación es requerida")
      .test("ccfId", "La caja de compensación es requerida", (value) => {
        return value !== "00000000-0000-0000-0000-000000000000";
      }),
    afpId: yup
      .string()
      .required("El fondo de pensiones es requerido")
      .test("afpId", "El fondo de pensiones es requerido", (value) => {
        return value !== "00000000-0000-0000-0000-000000000000";
      }),
    fcesId: yup
      .string()
      .required("El fondo de cesantías es requerido")
      .test("fcesId", "El fondo de cesantías es requerido", (value) => {
        return value !== "00000000-0000-0000-0000-000000000000";
      }),
    epsId: yup
      .string()
      .required("La EPS es requerida")
      .test("epsId", "La EPS es requerida", (value) => {
        return value !== "00000000-0000-0000-0000-000000000000";
      }),
    contracts: yup
      .array()
      .of(
        yup.object().shape({
          businessUnitId: yup.string().nullable().required("La empresa es requerida"),
          jobId: yup.string().nullable().required("El cargo es requerido"),
          contractTypeId: yup.string().nullable().required("El tipo de contrato es requerido"),
          temporaryCompany: yup.string().nullable().required("La empresa temporal es requerida"),
          contractStartDate: yup
            .date()
            .nullable()
            .typeError("Fecha inválida")
            .required("La fecha de inicio es requerida"),
          contractEndDate: yup
            .date()
            .nullable()
            .typeError("Fecha inválida")
            .min(yup.ref("contractStartDate"), "La fecha de fin debe ser mayor a la de inicio"),
        })
      )
      .min(1, "Debe agregar al menos un contrato")
      .required("Debe agregar al menos un contrato"),
  });

  const validationSchemaForEdit = yup.object().shape({
    email: yup.string().required("El email es requerido").email("El email es incorrecto"),
    phone: yup.object().shape({
      value: yup
        .string()
        .required("Telefono es requerido")
        .min(10, "El teléfono no tiene la longitud correcta")
        .test("phone_async_validation", "Error en el formato del país", (value, { parent, createError }: any): any =>
          validationRegExp(parent, createError)
        ),
    }),
  });

  const squema = disabled ? validationSchemaForEdit : validationSchemaForCreate;

  const initialValues: SurveyPerson = {
    identificationTypeId: model?.identificationTypeId || "",
    identificationNumber: model?.identificationNumber || 0,
    personNames: model?.personNames || "",
    personLastName: model?.personLastName || "",
    email: model?.email || "",
    phone: model?.phone || { value: "", dialCode: "+57" },
    ccfId: model?.ccfId || "",
    afpId: model?.afpId || "",
    fcesId: model?.fcesId || "",
    epsId: model?.epsId || "",
    userCreation: model?.userCreation || currentUser,
    updatedBy: model?.updatedBy || currentUser,
    contracts: model?.contracts || [],
  };

  useEffect(() => {
    const items = countryCodes?.map((p) => {
      const item: CountryAreaItem = {
        countryCode: p.iso2Code,
        regex: p.phoneNumberRegex,
        areaCode: p.areaCode,
      };
      return item;
    });
    setCountriesWithAreaCode(items);
    setCountries(items?.map((item) => item?.countryCode.toLowerCase()) ?? []);
  }, [countryCodes]);
  return (
    <Box
      sx={{
        width: 600,
        margin: "auto",
        maxWidth: "100%",
      }}
    >
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={squema}
        onSubmit={(values) => {
          let phone = values?.phone?.value;
          if (!values?.phone?.value?.includes("+")) {
            phone = `+${values?.phone?.value}`;
          }
          const valuesFormatted = { ...values, create: create || false, phone };
          handleSubmit(valuesFormatted);
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue }: any) => {
          return (
            <Form>
              <ContainerScrollable minHeight='690px'>
                <Select
                  mapKey='value'
                  margin='normal'
                  mapValue='text'
                  disabled={disabled}
                  id='identificationTypeId'
                  label='Tipo de documento'
                  name='identificationTypeId'
                  value={values.identificationTypeId}
                  onChange={(selectedOption: React.ChangeEvent<HTMLInputElement>) => {
                    handleChange(selectedOption);
                  }}
                  dataSource={identificationTypes}
                  error={touched.identificationTypeId && Boolean(errors.identificationTypeId)}
                  helperText={touched.identificationTypeId && errors.identificationTypeId}
                />
                <TextField
                  margin='normal'
                  disabled={disabled}
                  id='identificationNumber'
                  name='identificationNumber'
                  label='Número de documento'
                  value={values.identificationNumber}
                  error={touched.identificationNumber && Boolean(errors.identificationNumber)}
                  helperText={touched.identificationNumber && errors.identificationNumber}
                  onChange={handleChange}
                />
                <TextField
                  margin='normal'
                  label='Nombres'
                  id='personNames'
                  name='personNames'
                  disabled={disabled}
                  value={values.personNames}
                  onChange={handleChange}
                  error={touched.personNames && Boolean(errors.personNames)}
                  helperText={touched.personNames && errors.personNames}
                />
                <TextField
                  margin='normal'
                  label='Apellidos'
                  disabled={disabled}
                  id='personLastName'
                  name='personLastName'
                  value={values.personLastName}
                  onChange={handleChange}
                  error={touched.personLastName && Boolean(errors.personLastName)}
                  helperText={touched.personLastName && errors.personLastName}
                />
                <TextField
                  id='email'
                  name='email'
                  margin='normal'
                  label='Correo electrónico'
                  value={values.email}
                  onChange={handleChange}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />
                <PhoneInput
                  country='co'
                  name='phone'
                  key='dialCode'
                  formik={{ values, errors, handleBlur, setFieldValue }}
                  keyProp='dialCode'
                  countries={countries}
                />

                <Select
                  name='ccfId'
                  margin='normal'
                  disabled={disabled}
                  value={values?.ccfId}
                  mapKey='businessUnitId'
                  mapValue='businessUnitName'
                  label='Caja de compensación'
                  onChange={(selectedOption: React.ChangeEvent<HTMLInputElement>) => {
                    handleChange(selectedOption);
                  }}
                  dataSource={businessUnitsTypes[1]?.businessUnitType ?? []}
                  error={touched.ccfId && Boolean(errors.ccfId)}
                  helperText={touched.ccfId && errors.ccfId}
                />
                <Select
                  label='Pensiones'
                  margin='normal'
                  mapKey='businessUnitId'
                  mapValue='businessUnitName'
                  name='afpId'
                  disabled={disabled}
                  value={values?.afpId}
                  onChange={(selectedOption: React.ChangeEvent<HTMLInputElement>) => {
                    handleChange(selectedOption);
                  }}
                  dataSource={businessUnitsTypes[5]?.businessUnitType ?? []}
                  error={touched.afpId && Boolean(errors.afpId)}
                  helperText={touched.afpId && errors.afpId}
                />
                <Select
                  label='Cesantias'
                  margin='normal'
                  mapKey='businessUnitId'
                  mapValue='businessUnitName'
                  name='fcesId'
                  disabled={disabled}
                  value={values?.fcesId}
                  onChange={(selectedOption: React.ChangeEvent<HTMLInputElement>) => {
                    handleChange(selectedOption);
                  }}
                  dataSource={businessUnitsTypes[2]?.businessUnitType ?? []}
                  error={touched.fcesId && Boolean(errors.fcesId)}
                  helperText={touched.fcesId && errors.fcesId}
                />
                <Select
                  label='EPS'
                  margin='normal'
                  mapKey='businessUnitId'
                  mapValue='businessUnitName'
                  name='epsId'
                  disabled={disabled}
                  value={values?.epsId}
                  onChange={(selectedOption: React.ChangeEvent<HTMLInputElement>) => {
                    handleChange(selectedOption);
                  }}
                  dataSource={businessUnitsTypes[3]?.businessUnitType ?? []}
                  error={touched.epsId && Boolean(errors.epsId)}
                  helperText={touched.epsId && errors.epsId}
                />
                <br />
                <br />
                <FieldArray
                  name='contracts'
                  validateOnChange={false}
                  render={({ push, remove, form }: any) => {
                    const singleContract = form.values.contracts?.some((contract: any) => contract.canDelete);
                    const IfContractEnded = () =>
                      form?.values?.contracts?.some((contract: any) => contract?.contractEndDate === null);
                    return (
                      <>
                        {values?.contracts?.map((contract: any, index: number) => {
                          return (
                            <div key={index} style={{ width: "106%" }}>
                              <AccordionControlled
                                title='Contrato'
                                index={index}
                                remove={() => remove(index)}
                                disabled={!contract?.canDelete}
                              >
                                <Company
                                  jobs={jobs}
                                  index={index}
                                  errors={errors}
                                  values={values}
                                  touched={touched}
                                  disabled={disabled}
                                  businesses={businesses}
                                  setFieldValue={setFieldValue}
                                  contractTypes={contractTypes}
                                  handleChange={handleChange}
                                />
                              </AccordionControlled>
                            </div>
                          );
                        })}
                        {values?.contracts && (
                          <ErrorMessage>
                            {!Array.isArray(errors?.contracts)
                              ? errors?.contracts
                              : "Hay contratos con campos sin respuesta o con errores"}
                          </ErrorMessage>
                        )}
                        <Button
                          variant='outlined'
                          color='primary'
                          disabled={loading || singleContract || IfContractEnded()}
                          sx={{ mt: 2 }}
                          onClick={() => push({ ...contractModel })}
                        >
                          Agregar Contrato
                        </Button>
                      </>
                    );
                  }}
                />
              </ContainerScrollable>
              <ButtonContainer>
                <Link to='/survey/send'>
                  <Button type='button'>Volver</Button>
                </Link>
                <LoadingButton
                  size='small'
                  type='submit'
                  loading={loading}
                  loadingPosition='start'
                  startIcon={<SaveIcon />}
                  disabled={loading}
                >
                  <span>{submitButtonText}</span>
                </LoadingButton>
              </ButtonContainer>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};
const ButtonContainer = styled("div")`
  display: flex;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing(2, 0)};
`;

export default SurveyManageUsersForm;
